import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  createAppVersion,
  deleteAppVersion,
  listAppVersions,
  publishAppVersion,
  unpublishAppVersion,
} from '../../api/appVersions';

const { Text, Paragraph } = Typography;

const formatFileSize = (bytes) => {
  const value = Number(bytes || 0);
  if (value <= 0) {
    return '-';
  }
  if (value >= 1024 * 1024) {
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${(value / 1024).toFixed(1)} KB`;
};

const publishedTag = (published) =>
  published ? <Tag color="green">已发布</Tag> : <Tag>未发布</Tag>;

const forceTag = (forceUpdate) =>
  forceUpdate ? <Tag color="red">强制更新</Tag> : <Tag color="blue">普通更新</Tag>;

const AppVersions = () => {
  const [form] = Form.useForm();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyVersionId, setBusyVersionId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchVersions = async (nextPagination = pagination) => {
    setLoading(true);
    try {
      const result = await listAppVersions({
        page: nextPagination.current,
        size: nextPagination.pageSize,
      });
      setVersions(result.list || []);
      setPagination((prev) => ({
        ...prev,
        current: result.page || nextPagination.current,
        pageSize: result.size || nextPagination.pageSize,
        total: result.total || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch app versions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    form.resetFields();
    setFileList([]);
    form.setFieldsValue({
      forceUpdate: false,
      published: true,
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const apkFile = fileList[0]?.originFileObj;
      if (!apkFile) {
        message.error('请选择APK安装包');
        return;
      }

      setSaving(true);
      await createAppVersion({
        ...values,
        file: apkFile,
      });
      message.success('版本已上传');
      resetForm();
      await fetchVersions({ ...pagination, current: 1 });
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to create app version:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (record) => {
    if (busyVersionId !== null) return;
    setBusyVersionId(record.id);
    try {
      if (record.published) {
        await unpublishAppVersion(record.id);
        message.success('版本已取消发布');
      } else {
        await publishAppVersion(record.id);
        message.success('版本已发布');
      }
      await fetchVersions();
    } catch (error) {
      console.error('Failed to toggle app version:', error);
    } finally {
      setBusyVersionId(null);
    }
  };

  const handleDelete = async (record) => {
    if (busyVersionId !== null || record.published) return;
    setBusyVersionId(record.id);
    try {
      await deleteAppVersion(record.id);
      message.success('版本及安装包已删除');
      await fetchVersions({
        ...pagination,
        current: versions.length === 1 ? Math.max(1, pagination.current - 1) : pagination.current,
      });
    } catch (error) {
      console.error('Failed to delete app version:', error);
      // Reload persisted deletion status so an OSS failure exposes the retry action.
      await fetchVersions();
    } finally {
      setBusyVersionId(null);
    }
  };

  const beforeUpload = (file) => {
    const isApk = file.name?.toLowerCase().endsWith('.apk');
    if (!isApk) {
      message.error('请选择 .apk 安装包');
      return Upload.LIST_IGNORE;
    }
    setFileList([
      {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
      },
    ]);
    return false;
  };

  const columns = [
    {
      title: '版本',
      dataIndex: 'versionName',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.versionName}</Text>
          <Text type="secondary">versionCode {record.versionCode}</Text>
        </Space>
      ),
    },
    {
      title: '状态',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          {record.deletionPending ? <Tag color="orange">删除待重试</Tag> : publishedTag(record.published)}
          {forceTag(record.forceUpdate)}
        </Space>
      ),
    },
    {
      title: '安装包',
      dataIndex: 'apkFilename',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text>{record.apkFilename}</Text>
          <Text type="secondary">{formatFileSize(record.apkSizeBytes)}</Text>
        </Space>
      ),
    },
    {
      title: 'SHA-256',
      dataIndex: 'apkSha256',
      width: 260,
      render: (value) => (
        <Text code copyable style={{ whiteSpace: 'normal' }}>
          {value || '-'}
        </Text>
      ),
    },
    {
      title: '更新说明',
      dataIndex: 'releaseNotes',
      render: (value) => (
        <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: '展开' }} style={{ marginBottom: 0 }}>
          {value || '-'}
        </Paragraph>
      ),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '操作',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Popconfirm
            disabled={record.deletionPending || busyVersionId !== null}
            title={record.published ? '取消发布版本' : '发布版本'}
            description={record.published ? '取消发布后旧客户端不会再更新到此版本。' : '发布后低版本客户端会检测到该版本。'}
            onConfirm={() => handleTogglePublish(record)}
          >
            <Button size="small" icon={<UploadOutlined />} disabled={record.deletionPending || busyVersionId !== null}>
              {record.published ? '取消发布' : '发布'}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`删除版本 ${record.versionName}（${record.versionCode}）？`}
            description="将永久删除版本记录及对应APK安装包，删除后无法恢复。"
            okText="删除版本及安装包"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={record.published || busyVersionId !== null}
            onConfirm={() => handleDelete(record)}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              disabled={record.published || busyVersionId !== null}
              loading={busyVersionId === record.id}
              title={record.published ? '请先取消发布，再删除版本及安装包' : undefined}
            >
              {record.deletionPending ? '重试删除' : '删除'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>版本管理</h2>
          <Text type="secondary">上传 Android 安装包，控制普通更新或强制更新策略。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchVersions()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ forceUpdate: false, published: true }}
        >
          <Space align="start" wrap size={24}>
            <Form.Item
              label="versionCode"
              name="versionCode"
              rules={[{ required: true, message: '请输入versionCode' }]}
            >
              <InputNumber min={1} precision={0} style={{ width: 180 }} placeholder="例如 12" />
            </Form.Item>
            <Form.Item
              label="versionName"
              name="versionName"
              rules={[{ required: true, message: '请输入versionName' }]}
            >
              <Input style={{ width: 180 }} placeholder="例如 1.2.0" />
            </Form.Item>
            <Form.Item label="强制更新" name="forceUpdate" valuePropName="checked">
              <Switch checkedChildren="强更" unCheckedChildren="普通" />
            </Form.Item>
            <Form.Item label="立即发布" name="published" valuePropName="checked">
              <Switch checkedChildren="发布" unCheckedChildren="草稿" />
            </Form.Item>
          </Space>

          <Form.Item
            label="更新说明"
            name="releaseNotes"
            rules={[{ required: true, message: '请填写更新说明' }]}
          >
            <Input.TextArea rows={4} placeholder="逐条填写更新内容，客户端弹窗会展示给用户。" />
          </Form.Item>

          <Form.Item label="APK安装包" required>
            <Upload
              accept=".apk,application/vnd.android.package-archive"
              maxCount={1}
              beforeUpload={beforeUpload}
              fileList={fileList}
              onRemove={() => {
                setFileList([]);
                return true;
              }}
            >
              <Button icon={<CloudUploadOutlined />}>选择APK文件</Button>
            </Upload>
          </Form.Item>

          <Space>
            <Button type="primary" icon={<UploadOutlined />} loading={saving} onClick={handleSubmit}>
              上传版本
            </Button>
            <Button onClick={resetForm}>重置</Button>
          </Space>
        </Form>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={versions}
          scroll={{ x: 1380 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              const next = { current: page, pageSize, total: pagination.total };
              setPagination(next);
              fetchVersions(next);
            },
          }}
        />
      </Card>
    </Space>
  );
};

export default AppVersions;
